import { createContext, useContext, useEffect, useState } from "react";
import { auth } from "../firebase";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup
} from "firebase/auth";
import { sendPasswordResetEmail } from "firebase/auth";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState("User"); // Add role state
  const [loading, setLoading] = useState(true);
  const resetPassword = (email) => {
    return sendPasswordResetEmail(auth, email);
  };
  // ✅ Email login
  const loginWithEmail = (email, password) =>
    signInWithEmailAndPassword(auth, email, password);

  // ✅ Email signup
  const signupWithEmail = (email, password) =>
    createUserWithEmailAndPassword(auth, email, password);

  // ✅ Google login (WEB only)
  const loginWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    await signInWithPopup(auth, provider);
  };

  // ✅ Logout
  const logout = async () => {
    await signOut(auth);
    setUserRole("User"); // Reset role on logout
  };

  // ✅ Fetch user role from backend
  const fetchUserRole = async (firebaseUser) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/auth/google-login`, {

        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: firebaseUser.email,
          googleId: firebaseUser.uid
        })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('User role data:', data); // Debug log
        const role = data.user?.role || "User";
        setUserRole(role);
        console.log('Set user role to:', role); // Debug log
        return data.token; // Return JWT token
      } else {
        console.error('Failed to fetch user role:', response.status);
        setUserRole("User"); // Default to User on failure
      }
    } catch (error) {
      console.error('Error fetching user role:', error);
      setUserRole("User"); // Default to User on error
    }
    return null;
  };

  // ✅ Auth state listener
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async currentUser => {
      setUser(currentUser);
      console.log('Auth state changed - User:', currentUser?.email);
      
      if (currentUser) {
        // Fetch user role from backend when user logs in
        await fetchUserRole(currentUser);
        console.log('Fetched user role:', userRole);
      } else {
        setUserRole("User"); // Reset to default when logged out
        console.log('User logged out, role reset to User');
      }
      
      setLoading(false);
    });
    return unsub;
  }, []);

  const value = {
    user,
    userRole, // Add role to context
    isAdmin: userRole === "Admin", // Add admin check helper
    loginWithEmail,
    signupWithEmail,
    loginWithGoogle,
    resetPassword,
    logout,
    loading,
    refreshUserRole: () => user && fetchUserRole(user), // Add manual refresh
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
